import { useTranslation } from "react-i18next";
import { motion, type Variants } from "framer-motion";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Images, MapPin, Heart, Camera } from "lucide-react";
import Petals from "../common/Petals";
import Flourish from "../common/Flourish";
import SplitText from "../common/SplitText";
import { useSmoothScroll } from "../../providers/SmoothScroll";
import { fonts, palette } from "../../theme";

const EASE = [0.16, 1, 0.3, 1] as const;
const MAPS_URL =
  "https://maps.google.com/maps?q=Gr%C3%BCnberger%20Stra%C3%9Fe%20143%2C%2035394%20Gie%C3%9Fen";

// ── Styled ────────────────────────────────────────────────────

const FooterRoot = styled("footer")({
  position: "relative",
  overflow: "hidden",
  color: "#fff",
  padding: "120px 20px 40px",
  background:
    "linear-gradient(180deg, #2A211A 0%, #3A2E23 45%, #241C16 100%)",
  textAlign: "center",
});

const Bg = styled(Box)({
  position: "absolute",
  inset: "-6%",
  backgroundImage: "url('/assets/IMG_2585.jpeg')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  opacity: 0.22,
  animation: "kenburns 28s ease-in-out infinite alternate",
  zIndex: 0,
});

const Veil = styled(Box)({
  position: "absolute",
  inset: 0,
  zIndex: 1,
  background:
    "radial-gradient(120% 80% at 50% 30%, rgba(58,46,35,0.25) 0%, rgba(28,22,17,0.85) 100%)",
});

const Watermark = styled(Box)({
  position: "absolute",
  inset: 0,
  zIndex: 1,
  pointerEvents: "none",
  color: palette.gold,
  opacity: 0.06,
});

const Content = styled(motion.div)({
  position: "relative",
  zIndex: 3,
  maxWidth: 720,
  margin: "0 auto",
});

const ThankTitle = styled(Typography)({
  fontFamily: fonts.script,
  fontSize: "clamp(2.2rem, 6vw, 3.6rem)",
  lineHeight: 1.2,
  color: "#fff",
  textShadow: "0 4px 30px rgba(0,0,0,0.4)",
});

const Message = styled(Typography)({
  fontFamily: fonts.serif,
  fontSize: "1.08rem",
  lineHeight: 1.95,
  color: "rgba(255,255,255,0.88)",
  maxWidth: 560,
  margin: "20px auto 0",
});

const Signature = styled(Box)({
  fontFamily: fonts.script,
  fontSize: "clamp(2.4rem, 7vw, 3.8rem)",
  color: palette.champagne,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "18px",
  lineHeight: 1,
});

const DateLine = styled(Typography)({
  fontFamily: fonts.sans,
  fontSize: "0.8rem",
  letterSpacing: "0.24em",
  textTransform: "uppercase",
  color: palette.champagne,
});

const Quote = styled(motion.p)({
  fontFamily: fonts.serif,
  fontStyle: "italic",
  fontSize: "clamp(1.1rem, 2.6vw, 1.4rem)",
  color: "rgba(255,255,255,0.95)",
  maxWidth: 520,
  margin: "0 auto",
  textShadow: "0 2px 16px rgba(0,0,0,0.4)",
});

const AppreciationCard = styled(motion.div)({
  maxWidth: 460,
  margin: "0 auto",
  padding: "30px 28px",
  borderRadius: 18,
  background: "rgba(255,255,255,0.08)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(231,216,196,0.32)",
  boxShadow: "0 16px 40px rgba(0,0,0,0.3)",
});

const Social = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "16px",
});

const SocialBtn = styled(motion.button)({
  width: 46,
  height: 46,
  borderRadius: "50%",
  border: "1px solid rgba(231,216,196,0.4)",
  background: "rgba(255,255,255,0.06)",
  color: palette.champagne,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color .3s ease, border-color .3s ease, box-shadow .3s ease",
  "&:hover": {
    color: "#fff",
    borderColor: palette.gold,
    boxShadow: "0 0 20px rgba(191,160,106,0.45)",
  },
});

const SeeYou = styled(Typography)({
  fontFamily: fonts.display,
  fontSize: "clamp(1.1rem, 3vw, 1.6rem)",
  letterSpacing: "0.1em",
  color: "#fff",
});

const Copyright = styled(Typography)({
  fontFamily: fonts.sans,
  fontSize: "0.72rem",
  letterSpacing: "0.12em",
  color: "rgba(255,255,255,0.55)",
});

// ── Variants (révélation en cascade) ──────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};
const item: Variants = {
  hidden: { opacity: 0, y: 24, filter: "blur(6px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.9, ease: EASE } },
};

// ── Composant ─────────────────────────────────────────────────

export default function Footer() {
  const { t } = useTranslation();
  const scrollTo = useSmoothScroll();

  const socials = [
    { icon: <Images size={20} strokeWidth={1.6} />, label: "Galerie", action: () => scrollTo("#gallery") },
    { icon: <MapPin size={20} strokeWidth={1.6} />, label: "Lieu", action: () => window.open(MAPS_URL, "_blank", "noopener") },
    { icon: <Heart size={20} strokeWidth={1.6} />, label: "RSVP", action: () => scrollTo("#rsvp") },
    { icon: <Camera size={20} strokeWidth={1.6} />, label: "Album", action: () => scrollTo("#gallery") },
  ];

  return (
    <FooterRoot>
      <Bg aria-hidden />
      <Veil aria-hidden />
      <Watermark aria-hidden>
        <Box sx={{ position: "absolute", top: -30, left: -40, fontSize: 240 }}>❀</Box>
        <Box sx={{ position: "absolute", bottom: -50, right: -30, fontSize: 280 }}>❀</Box>
      </Watermark>

      {/* Pétales dorés */}
      <Petals count={12} opacity={0.55} />

      <Content
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
      >
        <motion.div variants={item}>
          <Flourish />
        </motion.div>

        <motion.div variants={item}>
          <ThankTitle>
            <SplitText text={t("footer.thankTitle")} stagger={0.03} />
          </ThankTitle>
        </motion.div>

        <motion.div variants={item}>
          <Message>{t("footer.message")}</Message>
        </motion.div>

        {/* Signature manuscrite */}
        <motion.div variants={item}>
          <Signature sx={{ my: 4 }}>
            <SplitText text={t("home.name2")} stagger={0.06} />
            <Box component="span" sx={{ color: palette.gold, fontSize: "0.6em", animation: "heartbeat 2.8s ease-in-out infinite" }}>
              ♡
            </Box>
            <SplitText text={t("home.name1")} stagger={0.06} delay={0.4} />
          </Signature>
        </motion.div>

        <motion.div variants={item}>
          <DateLine>{t("footer.dateLine")}</DateLine>
        </motion.div>

        <motion.div variants={item}>
          <Flourish width={180} />
        </motion.div>

        {/* Citation finale */}
        <Quote
          variants={item}
          style={{ marginTop: 8, marginBottom: 40 }}
        >
          “{t("footer.quote")}”
        </Quote>

        {/* Carte d'appréciation */}
        <AppreciationCard variants={item}>
          <Box sx={{ color: palette.gold, mb: 1.5, display: "flex", justifyContent: "center", animation: "heartbeat 2.6s ease-in-out infinite" }}>
            <Heart size={28} fill={palette.gold} strokeWidth={0.6} />
          </Box>
          <Typography sx={{ fontFamily: fonts.serif, fontSize: "1.05rem", lineHeight: 1.8, color: "rgba(255,255,255,0.9)" }}>
            {t("footer.appreciation")}
          </Typography>
        </AppreciationCard>

        {/* Liens */}
        <motion.div variants={item}>
          <Social sx={{ my: 4 }}>
            {socials.map((s) => (
              <SocialBtn
                key={s.label}
                type="button"
                aria-label={s.label}
                title={s.label}
                onClick={s.action}
                whileHover={{ scale: 1.12, y: -3 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 320, damping: 18 }}
              >
                {s.icon}
              </SocialBtn>
            ))}
          </Social>
        </motion.div>

        {/* Dernier message */}
        <motion.div variants={item}>
          <SeeYou>{t("footer.seeYou")}</SeeYou>
        </motion.div>
      </Content>

      {/* Bas de page */}
      <Box sx={{ position: "relative", zIndex: 3, mt: 6, pt: 3, borderTop: "1px solid rgba(231,216,196,0.15)" }}>
        <Copyright>{t("footer.madeWith")} — {t("footer.dateLine")}</Copyright>
        <Copyright sx={{ mt: 0.6 }}>
          © 2026{" "}
          <Box
            component="a"
            href="https://winorgroup.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: palette.champagne, textDecoration: "none", "&:hover": { color: "#fff" } }}
          >
            winorgroup.com
          </Box>{" "}
          · {t("footer.rights")}
        </Copyright>
      </Box>
    </FooterRoot>
  );
}
